import React, { useState, useEffect } from 'react';

const API_BASE = '/backend/scheduling/staff';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

interface StaffBooking {
  id: number;
  bookingUid: string;
  startTime: string;
  endTime: string;
  status: string;
  inviteeName: string;
  inviteeEmail: string;
  eventTitle: string;
  meetLink: string | null;
}

interface StaffMembership {
  staffId: number;
  businessName: string;
  title: string;
  upcomingBookings: StaffBooking[];
  pastBookings: StaffBooking[];
  customAvailability: any;
}

interface DashboardData {
  memberships: StaffMembership[];
  isStaff: boolean;
}

const StaffDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/my-dashboard`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to load dashboard');
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading your dashboard...</div>;
  }

  if (error) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>{error}</div>;
  }

  if (!data || !data.isStaff || data.memberships.length === 0) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>&#128197;</div>
        <h2 style={{ margin: '0 0 12px', fontSize: '1.3rem' }}>No Staff Memberships</h2>
        <p style={{ color: '#6b7280', margin: 0 }}>
          You haven't been added as staff to any business yet. Ask a business owner to invite you.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ margin: '0 0 24px', fontSize: '1.5rem', fontWeight: '600' }}>My Schedule</h1>

      {data.memberships.map(membership => (
        <div key={membership.staffId} style={{ marginBottom: '32px' }}>
          {/* Business header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600' }}>{membership.businessName}</h2>
            {membership.title && (
              <span style={{
                padding: '3px 10px', backgroundColor: '#eff6ff', color: '#3b82f6',
                borderRadius: '12px', fontSize: '0.8rem', fontWeight: '500'
              }}>
                {membership.title}
              </span>
            )}
          </div>

          {/* Upcoming bookings */}
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '500', color: '#374151' }}>
            Upcoming ({membership.upcomingBookings.length})
          </h3>

          {membership.upcomingBookings.length === 0 ? (
            <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: '0 0 20px' }}>
              No upcoming bookings
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {membership.upcomingBookings.map(booking => (
                <div key={booking.id} style={{
                  padding: '14px 16px', backgroundColor: 'white', borderRadius: '10px',
                  border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                }}>
                  <div style={{ minWidth: '180px' }}>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '0.95rem' }}>
                      {formatDateTime(booking.startTime)}
                    </p>
                    <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
                      {booking.eventTitle}
                    </p>
                  </div>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>{booking.inviteeName}</p>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.8rem' }}>{booking.inviteeEmail}</p>
                  </div>
                  {booking.meetLink && (
                    <a
                      href={booking.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '6px 14px', backgroundColor: '#059669', color: 'white',
                        borderRadius: '6px', textDecoration: 'none', fontSize: '0.8rem', fontWeight: '500',
                      }}
                    >
                      Join Meeting
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Past bookings */}
          {membership.pastBookings.length > 0 && (
            <>
              <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                Past ({membership.pastBookings.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
                {membership.pastBookings.map(booking => (
                  <div key={booking.id} style={{
                    padding: '10px 16px', backgroundColor: '#f9fafb', borderRadius: '8px',
                    border: '1px solid #f3f4f6', display: 'flex', alignItems: 'center',
                    gap: '16px', opacity: 0.8,
                  }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', minWidth: '160px' }}>
                      {formatDateTime(booking.startTime)}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.85rem', flex: 1 }}>
                      {booking.inviteeName} — {booking.eventTitle}
                    </p>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '500',
                      backgroundColor: booking.status === 'confirmed' || booking.status === 'completed'
                        ? '#f0fdf4' : booking.status === 'cancelled' ? '#fef2f2' : '#f3f4f6',
                      color: booking.status === 'confirmed' || booking.status === 'completed'
                        ? '#059669' : booking.status === 'cancelled' ? '#dc2626' : '#6b7280',
                    }}>
                      {booking.status}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ))}

      <style>{`
        @media (max-width: 600px) {
          div[style*="flexWrap"] { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
};

export default StaffDashboard;
